import { KeyboardEvent, MouseEvent, ReactNode } from 'react';

import { useGlobalCursor } from '../v2-hooks/useGlobalCursor';
import { useSortableItem } from './SortableList';

/** Props for a drag handle placed anywhere inside a SortableList item. */
export interface SortableItemHandleProps {
  readonly ariaLabel: string;
  readonly children: ReactNode;
}

// Handle interactions must not toggle ancestors (e.g. ExpandableSection headers): a press under
// the 5px activation distance is still delivered as a click, and those headers toggle on the
// Space/Enter keyup that ends a keyboard pickup or drop.
function stopPropagation(event: MouseEvent | KeyboardEvent) {
  event.stopPropagation();
}

/**
 * Renders the drag handle for the enclosing SortableList item; use when the handle lives in a
 * child component rather than in the item renderer.
 */
export function SortableItemHandle({ ariaLabel, children }: SortableItemHandleProps) {
  const { dragHandleProps, isDragging, isDisabled } = useSortableItem();

  // Keep the grabbing cursor while dragging, even after the pointer leaves the handle.
  useGlobalCursor('grabbing', isDragging);

  return (
    <span
      ref={dragHandleProps.setActivatorNodeRef}
      {...dragHandleProps.attributes}
      {...dragHandleProps.listeners}
      aria-label={ariaLabel}
      className={isDisabled ? undefined : 'cursor-ns-resize'}
      onClick={stopPropagation}
      onKeyUp={stopPropagation}
    >
      {children}
    </span>
  );
}
